<template>
  <modal :name="nombreModal" :adaptive="true" height="auto" width="560px">
    <div class="conf">
      <div class="modal-header conf-cabecera">
        <h4 v-if="creaEnServidor">{{ $t("crearEnServidorTitulo", { nombre: unaSola.nombre }) }}</h4>
        <h4 v-else-if="unaSola">{{ $t("unificarTitulo", { nombre: unaSola.nombre }) }}</h4>
        <h4 v-else>{{ $t("aplicarTodosTitulo") }}</h4>
      </div>

      <div class="modal-body conf-cuerpo">
        <!-- Lo que va a pasar, antes de pedir nada. Mismo orden que los demas modales de
             confirmar: primero el precio, luego la decision. -->
        <ul class="conf-consecuencias" v-if="unaSola">
          <li>{{ $t("unificarTodosLaLlaman", { nombre: unaSola.nombre }) }}</li>
          <li v-if="creaEnServidor">{{ $t("crearEnServidorExplica") }}</li>
          <li v-else>{{ ganaServidor ? $t("unificarPorServidor") : $t("unificarPorMayoria") }}</li>
          <li v-if="cambios.length">
            {{ $tc("unificarCambiaEn", cambios.length, { n: cambios.length }) }}
          </li>
          <li>{{ $t("igualarProductosSeQuedan") }}</li>
          <li>{{ $tc("seGuardaEnTerminales", terminales, { n: terminales }) }}</li>
        </ul>

        <ul class="conf-consecuencias" v-else>
          <li v-if="aplicables.length">
            {{ $tc("aplicarTodosEscribe", aplicables.length, { n: aplicables.length }) }}
          </li>
          <li v-if="saltadas.length">
            {{ $tc("aplicarTodosSaltadas", saltadas.length, { n: saltadas.length }) }}
          </li>
          <li>{{ $t("igualarProductosSeQuedan") }}</li>
          <li>{{ $tc("seGuardaEnTerminales", terminales, { n: terminales }) }}</li>
        </ul>

        <!-- Que se escribe en que terminal. Es lo que el boton suelto no decia: el nombre
             que se pierde en un terminal no vuelve con otro boton. -->
        <div class="igu-cambios" v-if="unaSola && cambios.length">
          <p class="igu-titulo">{{ $t("igualarQueCambia") }}</p>

          <ul class="igu-lista">
            <li v-for="t in cambios" :key="'t' + t.idTpv">
              <span class="igu-nombre">{{ t.nombreTpv }}</span>
              <span class="igu-antes">{{ antesDe(t) }}</span>
              <span class="igu-flecha">&rarr;</span>
              <span class="igu-despues">«{{ unaSola.nombre }}»</span>
            </li>
          </ul>

          <p class="conf-nota">{{ $t("unificarOtroNombreBueno") }}</p>
        </div>

        <div class="igu-cambios" v-if="!unaSola && aplicables.length">
          <p class="igu-titulo">{{ $t("igualarQueCambia") }}</p>

          <ul class="igu-lista">
            <li v-for="c in aplicables" :key="'c' + c.code">
              <span class="igu-nombre">«{{ c.nombre }}»</span>
              <span class="igu-donde">{{ $t("aplicarTodosFaltaEn", { terminales: faltaEn(c).join(", ") }) }}</span>
            </li>
          </ul>
        </div>

        <p class="conf-nota" v-if="!unaSola && !aplicables.length">{{ $t("aplicarTodosNada") }}</p>

        <p class="conf-error" v-if="error">{{ error }}</p>
      </div>

      <div class="modal-footer conf-pie">
        <button type="button" class="btn btn-claro" :disabled="ocupado" @click="cerrar()">
          {{ $t("cancelar") }}
        </button>
        <button type="button" class="btn btn-verde" :disabled="ocupado || !hayQueHacer" @click="confirmar()">
          {{ unaSola ? $t("unificar") : $t("aplicarATodos") }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
/**
 * Igualar categorias entre los terminales del negocio: "Unificar" una o "Aplicar a
 * todos".
 *
 * Sustituye al boton que escribia directamente desde el aviso. Las dos acciones llaman a
 * `syncBusinessCategories`, que escribe el nombre canonico de la ranura en todos los
 * terminales -y crea la fila donde no existe-. El canonico es el del servidor si lo tiene
 * y, si no, el mas repetido. Aqui se ensena ANTES que terminal pierde que nombre, porque
 * eso no se deshace con un boton: habria que renombrar a mano.
 *
 *  - Con code (Unificar): solo esa categoria, aunque tenga nombres distintos.
 *  - Sin code (Aplicar a todos): las que faltan en algun terminal. Las que tienen nombres
 *    distintos las salta el servidor -ahi hay que elegir, y elegir es del usuario-, asi
 *    que aqui se cuentan aparte.
 *
 * La prevision se calcula con lo que ya trae `getBusinessCategories` por terminal, con la
 * misma regla que el servidor. Como los demas modales de confirmar, este NO escribe:
 * emite `confirmar` con el code -vacio para todas- y la pantalla llama al endpoint.
 */
export default {
  name: "IgualarCategoriasModal",

  props: {
    nombreModal: { type: String, default: "modalIgualarCategorias" },
    // Terminales vivos del negocio, solo para el plural del aviso.
    terminales: { type: Number, default: 0 },
    ocupado: { type: Boolean, default: false }
  },

  data() {
    return {
      code: "",
      categorias: [],
      error: ""
    };
  },

  computed: {
    /**
     * La categoria a unificar, o null si es "Aplicar a todos". Comparacion de cadenas: el
     * code "0" es una tecla de verdad.
     */
    unaSola() {
      if (this.code === "") {
        return null;
      }

      let code = String(this.code);

      return this.categorias.find(c => String(c.code) === code) || null;
    },

    /**
     * Terminales donde el nombre va a cambiar. Los que ya lo tienen bien tambien se
     * escriben -se les toca `updated`-, pero para el camarero no cambia nada.
     */
    cambios() {
      if (!this.unaSola) {
        return [];
      }

      let nombre = this.unaSola.nombre;

      return (this.unaSola.terminales || []).filter(t => !t.existe || t.nombre !== nombre);
    },

    ganaServidor() {
      if (!this.unaSola) {
        return false;
      }

      let servidor = (this.unaSola.terminales || []).find(t => t.rol === "servidor");

      return !!servidor && servidor.nombre === this.unaSola.nombre;
    },

    // Lo que el servidor escribe con code vacio: con nombre, pendiente en algun terminal
    // y sin nombres enfrentados.
    // Las que el servidor no tiene tampoco: esas solo se crean en el pidiendolas una a una.
    aplicables() {
      return this.categorias.filter(
        c => c.nombre && c.faltaEn && !(c.otrosNombres && c.otrosNombres.length) && !(c.soloEn && c.soloEn.length)
      );
    },

    // Unificar de una que el servidor no tiene: es "crearla en el servidor".
    creaEnServidor() {
      return !!this.unaSola && !!this.unaSola.soloEn && this.unaSola.soloEn.length > 0;
    },

    saltadas() {
      return this.categorias.filter(
        c => c.nombre && c.otrosNombres && c.otrosNombres.length && !(c.soloEn && c.soloEn.length)
      );
    },

    hayQueHacer() {
      return this.unaSola ? true : this.aplicables.length > 0;
    }
  },

  methods: {
    /**
     * @param {string} code       Tecla de la categoria a unificar, o "" para todas.
     * @param {Array}  categorias Todas las del negocio, con su detalle por terminal.
     */
    abrir(code, categorias) {
      this.code = code === undefined || code === null ? "" : String(code);
      this.categorias = categorias || [];
      this.error = "";

      this.$modal.show(this.nombreModal);
    },

    cerrar() {
      this.$modal.hide(this.nombreModal);
    },

    mostrarError(mensaje) {
      this.error = mensaje || this.$t("errorInesperado");
    },

    antesDe(t) {
      if (!t.existe) {
        return this.$t("igualarSeCrea");
      }

      return t.nombre ? "«" + t.nombre + "»" : this.$t("igualarSinNombre");
    },

    faltaEn(c) {
      return (c.terminales || [])
        .filter(t => !t.existe || t.nombre === "")
        .map(t => t.nombreTpv);
    },

    confirmar() {
      if (!this.hayQueHacer) {
        return;
      }

      this.error = "";
      this.$emit("confirmar", this.code);
    }
  }
};
</script>

<style scoped>
@import "../confirmar-en-modal.css";

/* ---------------------------------------------------------------------------
   Solo de este modal: que se escribe en cada terminal. Misma lista que la
   prevision de ReindexarGrupoModal.
   --------------------------------------------------------------------------- */

.igu-titulo {
    margin: 0 0 8px;
    font-size: 0.94em;
    font-weight: 500;
    color: #2c3e50;
}

.igu-lista {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e6e9ef;
    border-radius: 3px;
}

.igu-lista li {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid #f0f2f6;
    font-size: 0.9em;
}

.igu-lista li:last-child {
    border-bottom: 0;
}

.igu-nombre {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #2c3e50;
}

/* Lo que se pierde, tachado y en gris; lo que queda, en turquesa como la tecla nueva
   del modal de reindexar. */
.igu-antes {
    flex: 0 0 auto;
    color: #8b96a3;
    text-decoration: line-through;
}

.igu-flecha {
    flex: 0 0 auto;
    color: #c3cbd5;
}

.igu-despues {
    flex: 0 0 auto;
    color: #00806d;
    font-weight: 600;
}

.igu-donde {
    flex: 0 1 auto;
    font-size: 0.92em;
    color: #96690f;
}
</style>



// WEBPACK FOOTER //
// src/pages/Widgets/Categorias/IgualarCategoriasModal.vue